import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
    Alert, CircularProgress, Breadcrumbs, Link, InputAdornment, IconButton, Divider,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import RestoreIcon from '@mui/icons-material/Restore';
import Chip from '@mui/material/Chip';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;
const getJwt = () => localStorage.getItem("feathers-jwt");

const ROLES = [
    { value: 'admin', label: 'Admin', color: '#e85a2c' },
    { value: 'gruppi', label: 'Gruppi', color: '#2196f3' }
];

export const UsersEditPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const notify = useNotify();

    const [formData, setFormData] = React.useState({
        email: '', nome: '', cognome: '', roles: 'gruppi', gruppiIds: [], status: true, changePassword: false, newPassword: '', confirmPassword: ''
    });

    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [originalData, setOriginalData] = React.useState({});
    const [hasChanges, setHasChanges] = React.useState(false);
    const [showNewPassword, setShowNewPassword] = React.useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);
    const [gruppi, setGruppi] = React.useState([]);
    const [loadingGruppi, setLoadingGruppi] = React.useState(false);
    const [currentGruppiIds, setCurrentGruppiIds] = React.useState([]);

    const loadGruppi = async () => {
        try {
            setLoadingGruppi(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-gruppo`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    $sort: { nome: 1 }
                }
            });
            // Escludi gruppo Admin (id=3) e Guest (id=5)
            const gruppiFiltered = (response.data.data || []).filter(g => g.id !== 3 && g.id !== 5);
            setGruppi(gruppiFiltered);
        } catch (error) {
            console.error("Errore caricamento gruppi:", error);
        } finally {
            setLoadingGruppi(false);
        }
    };

    const loadUserGruppi = async () => {
        try {
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-users-gruppo`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: { usersId: userId }
            });
            if (response.data.data && response.data.data.length > 0) {
                const gruppiIds = response.data.data.map(item => item.tbl_gruppoId);
                setCurrentGruppiIds(gruppiIds);
                return gruppiIds;
            }
            return [];
        } catch (error) {
            console.error("Errore caricamento gruppi utente:", error);
            return [];
        }
    };

    const loadData = async () => {
        try {
            const jwt = getJwt();
            const response = await axios.get(`${host}/users/${userId}`, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });

            const user = response.data;

            // Carica gruppi utente
            const loadedGruppiIds = await loadUserGruppi();

            const formDataFromUser = {
                email: user.email || '',
                nome: user.nome || '',
                cognome: user.cognome || '',
                roles: user.roles || 'gruppi',
                gruppiIds: loadedGruppiIds,
                status: user.status !== undefined ? user.status : true,
                changePassword: false,
                newPassword: '',
                confirmPassword: ''
            };

            setFormData(formDataFromUser);
            setOriginalData(formDataFromUser);
        } catch (error) {
            notify("Errore nel caricamento", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const validatePasswordFields = () => {
        const newErrors = {};

        if (formData.changePassword) {
            if (!formData.newPassword) {
                newErrors.newPassword = 'Inserisci la nuova password';
            } else if (formData.newPassword.length < 6) {
                newErrors.newPassword = 'La password deve essere di almeno 6 caratteri';
            }

            if (!formData.confirmPassword) {
                newErrors.confirmPassword = 'Conferma la password';
            } else if (formData.newPassword !== formData.confirmPassword) {
                newErrors.confirmPassword = 'Le password non coincidono';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFieldChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));

        // Se cambia il ruolo, resetta gruppiIds
        if (name === 'roles') {
            setFormData(prev => ({ ...prev, gruppiIds: [] }));
        }

        // Validazione password in tempo reale
        if (name === 'newPassword' || name === 'confirmPassword' || name === 'changePassword') {
            setTimeout(() => validatePasswordFields(), 100);
        }
    };

    const handleSave = async () => {
        if (formData.changePassword && !validatePasswordFields()) {
            notify("Correggere gli errori nel form", { type: 'error' });
            return;
        }

        try {
            setSaving(true);
            const jwt = getJwt();
            const dataToSend = {
                email: formData.email.trim().toLowerCase(),
                nome: formData.nome.trim(),
                cognome: formData.cognome.trim(),
                roles: formData.roles,
                status: formData.status ? 1 : 0
            };

            // Aggiungi password solo se richiesto cambio
            if (formData.changePassword && formData.newPassword) {
                dataToSend.password = formData.newPassword;
            }

            await axios.patch(`${host}/users/${userId}`, dataToSend, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });

            // Gestione gruppi
            const newGruppiIds = formData.roles === 'admin' ? [3] : formData.gruppiIds;

            // Confronta gruppi correnti con nuovi
            const gruppiToAdd = newGruppiIds.filter(id => !currentGruppiIds.includes(id));
            const gruppiToRemove = currentGruppiIds.filter(id => !newGruppiIds.includes(id));

            // Rimuovi associazioni non più presenti
            for (const gruppoId of gruppiToRemove) {
                const assocResponse = await axios.get(`${host}/tbl-users-gruppo`, {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: { usersId: userId, tbl_gruppoId: gruppoId }
                });
                if (assocResponse.data.data && assocResponse.data.data.length > 0) {
                    await axios.delete(`${host}/tbl-users-gruppo/${assocResponse.data.data[0].id}`, {
                        headers: { 'Authorization': `Bearer ${jwt}` }
                    });
                }
            }

            // Aggiungi nuove associazioni
            for (const gruppoId of gruppiToAdd) {
                await axios.post(`${host}/tbl-users-gruppo`, {
                    usersId: parseInt(userId),
                    tbl_gruppoId: parseInt(gruppoId),
                    flVisibile: 1,
                    flAttivo: 1,
                    flAnnullato: 0,
                    ordinamento: 0
                }, {
                    headers: { 'Authorization': `Bearer ${jwt}` }
                });
            }

            notify(`Utente aggiornato con successo`, { type: 'success' });
            navigate('/users');
        } catch (error) {

            console.error("Errore aggiornamento:", error);
            if (error.response?.data?.errors?.some(e => e.message === 'email must be unique')) {
                setErrorDialogOpen(true);
            } else {
                notify("Errore nell'aggiornamento", { type: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    React.useEffect(() => {
        loadData();
        loadGruppi();
    }, [userId]);
    React.useEffect(() => {
        const changed = JSON.stringify({
            email: formData.email,
            nome: formData.nome,
            cognome: formData.cognome,
            roles: formData.roles,
            status: formData.status
        }) !== JSON.stringify({
            email: originalData.email,
            nome: originalData.nome,
            cognome: originalData.cognome,
            roles: originalData.roles,
            status: originalData.status
        }) || formData.changePassword || JSON.stringify(formData.gruppiIds.sort()) !== JSON.stringify(currentGruppiIds.sort());
        setHasChanges(changed);
    }, [formData, originalData, currentGruppiIds]);

    if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

    const selectedRole = ROLES.find(r => r.value === formData.roles);
    const hasErrors = Object.keys(errors).length > 0;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>


                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        <PersonIcon sx={{ fontSize: 32, verticalAlign: 'middle', mr: 1 }} />
                        Modifica Utente
                    </Typography>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')}>Indietro</Button>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Informazioni Utente</Typography>
                                </Box>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <TextField fullWidth label="Nome *" value={formData.nome} onChange={(e) => handleFieldChange('nome', e.target.value)} autoComplete="off" />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField fullWidth label="Cognome *" value={formData.cognome} onChange={(e) => handleFieldChange('cognome', e.target.value)} autoComplete="off" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Email *" type="email" value={formData.email} onChange={(e) => handleFieldChange('email', e.target.value)}
                                            autoComplete="off"
                                            InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment> }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel>Ruolo</InputLabel>
                                            <Select value={formData.roles} label="Ruolo" onChange={(e) => handleFieldChange('roles', e.target.value)}>
                                                {ROLES.map(role => (
                                                    <MenuItem key={role.value} value={role.value}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: role.color }} />
                                                            {role.label}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {formData.roles === 'gruppi' && (
                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel>Gruppi</InputLabel>
                                                <Select
                                                    multiple
                                                    value={formData.gruppiIds}
                                                    label="Gruppi"
                                                    onChange={(e) => handleFieldChange('gruppiIds', e.target.value)}
                                                    disabled={loadingGruppi}
                                                    renderValue={(selected) => (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => {
                                                                const gruppo = gruppi.find(g => g.id == value);
                                                                return (
                                                                    <Chip key={value} label={gruppo?.nome || value} size="small" />
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                >
                                                    {loadingGruppi ? (
                                                        <MenuItem disabled>
                                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                                            Caricamento...
                                                        </MenuItem>
                                                    ) : (
                                                        gruppi.map(gruppo => (
                                                            <MenuItem key={gruppo.id} value={gruppo.id}>
                                                                {gruppo.nome}
                                                            </MenuItem>
                                                        ))
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    )}

                                    {formData.roles === 'admin' && (
                                        <Grid item xs={12}>
                                            <Alert severity="info">
                                                L'utente Admin è collegato al gruppo "Admin" (ID: 3)
                                            </Alert>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <LockIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Cambio Password</Typography>
                                </Box>
                                <FormControlLabel
                                    control={<Switch checked={formData.changePassword} onChange={(e) => handleFieldChange('changePassword', e.target.checked)} />}
                                    label="Cambia password"
                                    sx={{ mb: 2 }}
                                />
                                {formData.changePassword && (
                                    <>
                                        <Divider sx={{ mb: 3 }} />
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Nuova Password *"
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    value={formData.newPassword}
                                                    onChange={(e) => handleFieldChange('newPassword', e.target.value)}
                                                    error={!!errors.newPassword}
                                                    helperText={errors.newPassword || "Minimo 6 caratteri"}
                                                    autoComplete="new-password"
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start"><LockIcon /></InputAdornment>,
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Conferma Password *"
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    value={formData.confirmPassword}
                                                    onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                                                    error={!!errors.confirmPassword}
                                                    helperText={errors.confirmPassword}
                                                    autoComplete="new-password"
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start"><LockIcon /></InputAdornment>,
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Anteprima</Typography>
                                <PersonIcon sx={{ fontSize: 60, color: selectedRole?.color || '#2196f3', mb: 2 }} />
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    {formData.nome} {formData.cognome}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {formData.email}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: selectedRole?.color }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRole?.label}</Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Impostazioni</Typography>
                                <FormControlLabel control={<Switch checked={formData.status} onChange={(e) => handleFieldChange('status', e.target.checked)} />} label="Utente attivo" />
                            </CardContent>
                        </Card>

                        {hasErrors && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Correggere i seguenti errori:</Typography>
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    {Object.values(errors).map((error, index) => (
                                        <li key={index}><Typography variant="body2">{error}</Typography></li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button variant="contained" size="large" startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave} disabled={!hasChanges || saving || hasErrors} sx={{ py: 1.5 }}>
                                {saving ? 'Salvataggio...' : 'Salva Modifiche'}
                            </Button>
                            <Button variant="outlined" startIcon={<RestoreIcon />} onClick={() => setFormData(originalData)} disabled={!hasChanges || saving}>
                                Ripristina
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </motion.div>

            <Dialog
                open={errorDialogOpen}
                onClose={() => setErrorDialogOpen(false)}
            >
                <DialogTitle>Errore Aggiornamento Utente</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        L'email è già adoperata su altro account
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setErrorDialogOpen(false)} color="primary" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};
